import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { IUserPathEntryComponent } from '../models/strapi.models';

const USER_PATH_KEY = 'user-path';
const MAX_PATH_LENGTH = 20;

/**
 * Get device type based on user agent and screen width
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';

  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();

  if (width < 768 || /mobile|android|iphone/.test(userAgent)) {
    return 'mobile';
  } else if (width < 1024 || /tablet|ipad/.test(userAgent)) {
    return 'tablet';
  }
  return 'desktop';
};

/**
 * Custom hook to track user navigation path throughout the session.
 * Stores the path in sessionStorage and automatically cleans up on component unmount.
 *
 * Usage: Call this hook once in _app.tsx to track all navigation
 */
export const useUserPathTracking = () => {
  const router = useRouter();
  const isInitialMount = useRef(true);

  useEffect(() => {
    const addToPath = (path: string, isInitial = false) => {
      if (typeof window === 'undefined') return;
      const pathEntry: IUserPathEntryComponent = {
        url: path,
        title: document.title || 'Untitled',
        timestamp: new Date(),
        locale: router.locale || 'en',
        device: getDeviceType(),
      };

      // Add referrer only on first visit
      if (isInitial && document.referrer) {
        pathEntry.referrer = document.referrer;
      }

      const stored = sessionStorage.getItem(USER_PATH_KEY);
      const currentPath: IUserPathEntryComponent[] = stored
        ? JSON.parse(stored)
        : [];
      const newPath = [...currentPath, pathEntry].slice(-MAX_PATH_LENGTH);
      sessionStorage.setItem(USER_PATH_KEY, JSON.stringify(newPath));
    };

    // Add initial route on first mount
    if (isInitialMount.current) {
      addToPath(window.location.href, true);
      isInitialMount.current = false;
    }

    // Listen to route changes
    const handleRouteChange = (url: string) => {
      // Pequeño delay para asegurar que el título se ha actualizado
      setTimeout(() => {
        addToPath(url);
      }, 100);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    // Cleanup
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath, router.locale]);
};

/**
 * Get the current user navigation path.
 * @returns Array of visited pages with URL, title, timestamp, locale, device and referrer
 */
export const getUserPath = (): IUserPathEntryComponent[] => {
  if (typeof window === 'undefined') return [];
  const stored = sessionStorage.getItem(USER_PATH_KEY);
  return stored ? JSON.parse(stored) : [];
};

/**
 * Clear the user navigation path from sessionStorage.
 * Call this after submitting the contact form or when you want to reset tracking.
 */
export const clearUserPath = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(USER_PATH_KEY);
};
